// Hand-authored overlay on the DB catalog: scriptural anchors, song variant
// grouping, channel info, and content-curation choices that don't belong in
// the DB schema (yet). Edited freely; merged into the runtime catalog by
// src/lib/catalog.js.
//
// When a field eventually graduates into Supabase, remove it here.

// Curated films — single unified grid on /watch. Order is editorial:
// Devi storyline first (Mahamaya → Kali — episodes 1 & 2 of the same arc),
// Shiva-Sati next, then a music breath, then the explainer / mantra pieces,
// Heart Sutra closes the room quietly.
export const films = [
  {
    id: "mahamaya",
    title: "Durga · Episode 1 · The Birth of Mahamaya",
    subtitle: "Devi Mahatmyam · Markandeya Purana",
    description:
      "The supreme goddess emerges. The combined energies of all the gods coalesce into a single feminine form to meet what no single deity could.",
    youtube_video_id: "cGe1bnntA5Y",
    duration: "10m 58s",
    channel: "@dharmaweaveepics",
    published: "2026-04-23",
  },
  {
    id: "durga-kali",
    title: "Durga · Episode 2 · Kali's Origin Story",
    subtitle: "Devi Mahatmyam · Markandeya Purana",
    description:
      "When Mahamaya's gentleness was not enough, the goddess turned dark — and Kali stepped from her brow.",
    youtube_video_id: "xqtZOpYR0sI",
    channel: "@dharmaweaveepics",
  },
  {
    id: "shiva-sati",
    title: "Shiva & Sati · Origin Story",
    subtitle: "Skanda Purāṇa · Sati Khanda",
    description:
      "Sati's love. Daksha's pride. The fire that broke the world and woke Shiva to his rage.",
    youtube_video_id: "9ec3n6vs_tM",
    channel: "@dharmaweaveepics",
  },
  {
    id: "giridhar-gopal",
    title: "Mere to Giridhar Gopal",
    subtitle: "Meera Bhajan · Reimagined",
    description:
      "Mira's love song to Krishna, reborn in a new arrangement. 8 million listeners and counting.",
    youtube_video_id: "sCRMnR91mVI",
    channel: "@dharmaweave",
  },
  {
    id: "devi-maha-mantra",
    title: "Devi Maha Mantra · Explained",
    subtitle: "Satsang with Maya & Kabir · Ep 2",
    description:
      "Om Aim Hreem Kleem — the bīja mantras of Saraswati, Mahalakshmi, and Mahakali, opened slowly.",
    youtube_video_id: "S58GTyTNgtk",
    channel: "@dharmaweaveepics",
  },
  {
    id: "heart-sutra",
    title: "The Heart Sutra · Explained",
    subtitle: "Prajñāpāramitā · A visual meditation",
    description:
      "Form is emptiness, emptiness is form. Shariputra's koan, painted slowly into understanding.",
    youtube_video_id: "Vr_RJEbeT6U",
    channel: "@dharmaweaveepics",
  },
];

// Backward-compat alias — Home.jsx renders this as its featured player.
// Whatever sits at films[0] is the canonical "current featured" piece.
export const featuredFilm = films[0];

// Digital shop products — two types of catalog entry:
//
//   1. External-storefront product: outbound links to Etsy (digital download)
//      + Gelato (POD physical print) + (future) others. No native checkout
//      on dharmaweave.com — same pattern as the books pages, which link out
//      to Amazon / Google Play. Use this until our own LS native checkout
//      is verified, OR for products we don't sell directly (e.g. POD prints).
//
//   2. Lemon Squeezy native checkout product: the overlay checkout opens
//      on dharmaweave.com itself, LS handles tax/payments. Set
//      `ls_variant_id` to flip a product into this mode.
//
// LS handles all global tax (VAT/GST/sales-tax) as merchant of record. Etsy
// also handles tax for marketplace sales (sales-tax in covered US states,
// EU VAT, etc.). Gelato is a fulfillment partner — when listed via Shopify
// or marketplace integration, the parent platform handles tax.
// 8 pieces (Krishna + 7 haiku/zen), deduplicated. Pieces with 2 versions
// (Pure Art + Museum Edition) show both as buy buttons. Featured-version
// image (thumb) shown on the tile — 6 haikus featured as Pure Art, 1
// (Old Pond) featured as Museum Edition for visual variety / kanji preview.
// Updated 2026-05-07.
export const digitalProducts = [
  {
    id: "krishna-beautiful-portrait",
    kind: "wall_art",
    title: "Krishna · Beautiful Portrait",
    subtitle: "Hindu devotional · sacred portrait",
    description: "Bejeweled, peacock-crowned Krishna in a soft, contemplative gaze. Painted in the South Asian sacred art tradition of layered oil-glaze illumination.",
    hero_image: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/shop/krishna-beautiful-portrait-thumb.jpg",
    orientation: "portrait",
    tradition: "hindu",
    palette: "cosmic-gold",
    external_storefronts: [
      {
        store: "etsy-single",
        label: "Buy on Etsy",
        sublabel: "Premium matte · free shipping in the US",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500735344/lord-krishna-wall-art-hindu-devotional-print-sacred-indian-meditation-decor",
      },
    ],
  },
  {
    id: "basho-old-pond",
    kind: "wall_art",
    title: "Bashō · Old Pond",
    subtitle: "Japanese haiku · woodblock tradition",
    description: "\"Old pond — a frog jumps in, sound of water.\" — Matsuo Bashō, 1686",
    hero_image: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/shop/basho-old-pond-museum-edition-thumb.jpg",
    orientation: "portrait",
    tradition: "zen",
    palette: "cool-blue-purity",
    external_storefronts: [
      {
        store: "etsy-pure-art",
        label: "Pure Art",
        sublabel: "Edge-to-edge artwork · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500748574/japanese-woodblock-print-old-pond-haiku-frog-art-minimalist-zen-meditation-decor",
      },
      {
        store: "etsy-museum-edition",
        label: "Museum Edition",
        sublabel: "With Japanese kanji border · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500749200/japanese-woodblock-print-old-pond-haiku-frog-art-minimalist-zen-meditation-decor",
      },
    ],
  },
  {
    id: "issa-snail-mt-fuji",
    kind: "wall_art",
    title: "Issa · Snail and Mount Fuji",
    subtitle: "Japanese haiku · woodblock tradition",
    description: "\"O snail — climb Mount Fuji, but slowly, slowly.\" — Kobayashi Issa",
    hero_image: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/shop/issa-snail-mt-fuji-pure-art-thumb.jpg",
    orientation: "portrait",
    tradition: "zen",
    palette: "cool-blue-purity",
    external_storefronts: [
      {
        store: "etsy-pure-art",
        label: "Pure Art",
        sublabel: "Edge-to-edge artwork · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500775154/japanese-woodblock-print-mount-fuji-snail-haiku-edo-mokuhanga-art",
      },
      {
        store: "etsy-museum-edition",
        label: "Museum Edition",
        sublabel: "With Japanese kanji border · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500776562/japanese-woodblock-print-mount-fuji-snail-haiku-edo-mokuhanga-art-with-japanese-",
      },
    ],
  },
  {
    id: "basho-summer-grasses",
    kind: "wall_art",
    title: "Bashō · Summer Grasses",
    subtitle: "Japanese haiku · woodblock tradition",
    description: "\"Summer grasses — all that remains of warriors' dreams.\" — Matsuo Bashō",
    hero_image: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/shop/basho-summer-grasses-pure-art-thumb.jpg",
    orientation: "landscape",
    tradition: "zen",
    palette: "cool-blue-purity",
    external_storefronts: [
      {
        store: "etsy-pure-art",
        label: "Pure Art",
        sublabel: "Edge-to-edge artwork · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500765509/japanese-woodblock-print-basho-summer-grasses-hiroshige-landscape-wabi-sabi-trad",
      },
      {
        store: "etsy-museum-edition",
        label: "Museum Edition",
        sublabel: "With Japanese kanji border · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500775864/japanese-woodblock-print-basho-summer-grasses-hiroshige-landscape-wabi-sabi-trad",
      },
    ],
  },
  {
    id: "shiki-persimmon-horyuji",
    kind: "wall_art",
    title: "Shiki · Persimmon at Hōryū-ji",
    subtitle: "Japanese haiku · woodblock tradition",
    description: "\"Biting into a persimmon — the bell rings at Hōryū-ji.\" — Masaoka Shiki, 1895",
    hero_image: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/shop/shiki-persimmon-horyuji-pure-art-thumb.jpg",
    orientation: "portrait",
    tradition: "zen",
    palette: "cool-blue-purity",
    external_storefronts: [
      {
        store: "etsy-pure-art",
        label: "Pure Art",
        sublabel: "Edge-to-edge artwork · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500776340/japanese-woodblock-print-autumn-haiku-persimmon-horyuji-temple-kanji-decor",
      },
      {
        store: "etsy-museum-edition",
        label: "Museum Edition",
        sublabel: "With Japanese kanji border · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500765863/japanese-woodblock-print-autumn-haiku-persimmon-horyuji-temple-kanji-decor-with-",
      },
    ],
  },
  {
    id: "basho-crow-bare-branch",
    kind: "wall_art",
    title: "Bashō · Crow on a Bare Branch",
    subtitle: "Japanese haiku · woodblock tradition",
    description: "\"On a bare branch, a crow has alighted — autumn evening.\" — Matsuo Bashō",
    hero_image: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/shop/basho-crow-bare-branch-pure-art-thumb.jpg",
    orientation: "portrait",
    tradition: "zen",
    palette: "cool-blue-purity",
    external_storefronts: [
      {
        store: "etsy-pure-art",
        label: "Pure Art",
        sublabel: "Edge-to-edge artwork · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500765567/japanese-woodblock-crow-print-autumn-sunset-haiku-art-edo-mokuhanga-meditation-d",
      },
      {
        store: "etsy-museum-edition",
        label: "Museum Edition",
        sublabel: "With Japanese kanji border · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500766291/japanese-woodblock-crow-print-autumn-sunset-haiku-art-edo-mokuhanga-meditation-d",
      },
    ],
  },
  {
    id: "basho-stillness-cicada-rock",
    kind: "wall_art",
    title: "Bashō · Stillness, Cicada in Rock",
    subtitle: "Japanese haiku · woodblock tradition",
    description: "\"Stillness — the cicada's cry penetrates the rock.\" — Matsuo Bashō, 1689",
    hero_image: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/shop/basho-stillness-cicada-rock-pure-art-thumb.jpg",
    orientation: "portrait",
    tradition: "zen",
    palette: "cool-blue-purity",
    external_storefronts: [
      {
        store: "etsy-pure-art",
        label: "Pure Art",
        sublabel: "Edge-to-edge artwork · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500775466/japanese-woodblock-print-basho-stillness-haiku-cicada-art-wabi-sabi-forest-decor",
      },
      {
        store: "etsy-museum-edition",
        label: "Museum Edition",
        sublabel: "With Japanese kanji border · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500765427/japanese-woodblock-print-basho-stillness-haiku-cicada-art-wabi-sabi-forest-decor",
      },
    ],
  },
  {
    id: "dogen-boatman-cherry-petals",
    kind: "wall_art",
    title: "Dōgen · Flowers Fall, Boatman & Cherry Petals",
    subtitle: "Japanese haiku · woodblock tradition",
    description: "\"Flowers fall, even though we love them; weeds grow, even though we wish them away.\" — Eihei Dōgen",
    hero_image: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/shop/dogen-boatman-cherry-petals-pure-art-thumb.jpg",
    orientation: "landscape",
    tradition: "zen",
    palette: "cosmic-gold",
    external_storefronts: [
      {
        store: "etsy-pure-art",
        label: "Pure Art",
        sublabel: "Edge-to-edge artwork · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500754314/japanese-woodblock-print-cherry-blossom-haiku-dogen-zen-quote-spring-lake-japand",
      },
      {
        store: "etsy-museum-edition",
        label: "Museum Edition",
        sublabel: "With Japanese kanji border · printed & shipped",
        price_text: "from $35",
        url: "https://www.etsy.com/listing/4500744731/japanese-woodblock-print-cherry-blossom-haiku-dogen-zen-quote-spring-lake-japand",
      },
    ],
  },
];

// Scriptural anchors — for cards and detail pages. Cite the source so the
// teaching is *located*, not floating. Maya owns these — review and refine.
export const bookAnchors = {
  devi: {
    source: "Devi Mahatmyam · Markandeya Purana",
    verse: "1.71–87",
    line: "Yā devī sarva-bhūteṣu śakti-rūpeṇa saṃsthitā",
    translation: "She who, in all beings, abides as power.",
  },
  hanuman: {
    source: "Sundara Kanda · Valmiki Ramayana",
    verse: "5.1",
    line: "Tato rāvaṇa-nītāyāḥ sītāyāḥ śatru-karṣaṇaḥ",
    translation:
      "Then he who pulls down enemies, sought Sita stolen by Ravana.",
  },
  "shiva-sati": {
    source: "Shiva Purana · Rudra Samhita",
    verse: "Sati Khanda 30",
    line: "Patyuḥ pāda-rajaḥ-snātā sā devī yajña-kuṇḍa-gā",
    translation:
      "Bathed in the dust of her lord's feet, the goddess walked into the fire pit of the sacrifice.",
  },
  jalandhara: {
    source: "Padma Purana · Uttara Khanda",
    verse: "3–6",
    line: "Vṛndāyāḥ pātivratyena rakṣito jalajāsuraḥ",
    translation:
      "By Vrinda's vow of devotion was the ocean-born asura kept invincible.",
  },
  "hanuman-chalisa": {
    source: "Hanuman Chalisa · Tulsidas (Awadhi, 16th c.)",
    verse: "Doha · 40 chaupais",
    line: "Jai Hanuman gyān guṇ sāgar · Jai kapīs tihu-loka ujāgar",
    translation:
      "Hail Hanuman, ocean of knowledge and virtue · Hail lord of monkeys, illuminator of the three worlds.",
  },
  varaha: {
    source: "Gīta Govinda · Daśāvatāra-stotra (Jayadeva)",
    verse: "Varāha · v.3",
    line: "Vasati daśana-śikhare dharaṇī tava lagnā",
    translation:
      "The Earth clings to the tip of Your tusk, like the dark mark upon the moon. Victory, O Keshava, in the form of the Boar!",
  },
  "ox-herder": {
    source: "Ten Ox-Herding Pictures · Kuoan Shiyuan (12th c.)",
    verse: "Picture I · Searching for the Ox",
    line: "Nothing has been lost.",
    translation:
      "Alone in the endless grass, he pushes through, searching. The rivers widen, the mountains stretch away, the trail goes on.",
  },
};

// Per-book palette mood (per Maya/Kabir's brief). Three palette worlds:
//   - "cosmic-gold"     metaphysical / cosmic
//   - "earth-sunlight"  heroic / battle / dharmic action
//   - "cool-blue"       sage / meditative
//
// `accent_hex` is the gold tone the page leans into (overrides the global
// dharma-gold for buttons + rules in book detail).
export const bookPalettes = {
  "hanuman-chalisa": { mood: "earth-sunlight", accent_hex: "#c9782b" },
  hanuman:           { mood: "earth-sunlight", accent_hex: "#a35a2a" },
  devi:              { mood: "cosmic-gold",    accent_hex: "#d9a24f" },
  "shiva-sati":      { mood: "cool-blue",      accent_hex: "#1b7fa3" },
  jalandhara:        { mood: "earth-sunlight", accent_hex: "#a35a2a" },
  varaha:            { mood: "cosmic-gold",    accent_hex: "#d9a24f" },
  "samudra-manthan": { mood: "cosmic-gold",    accent_hex: "#c9782b" },
  "ox-herder":       { mood: "earth-sunlight", accent_hex: "#c9a45c" },
};

// Painterly hero art per book. Used as the full-bleed hero on /books/:slug.
// Falls back to the cover image when no hero is set. Maya delivers these
// as 1600x900 (16:9) painterly art over time.
export const bookHeroes = {
  // For Hanuman Chalisa we re-use the cover as the painterly hero until Maya
  // provides a dedicated 16:9 painterly piece. The cover IS painterly art.
  "hanuman-chalisa":
    "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/hanuman-chalisa/cover.jpg",
  // Gate I plate — the boy in silver grass at sunset. 1600x900, painterly.
  "ox-herder":
    "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/ox-herder/hero.jpg",
};

// When the hero image already has the book's title (and other text) printed
// onto it — like a cover — suppress the page's overlaid <h1> and scriptural
// anchor line so we don't get text-on-text overlap. Hanuman's cover has
// "HANUMAN" + author credit + tagline rendered into the artwork itself.
export const bookCoverHasText = new Set(["hanuman", "varaha", "samudra-manthan", "ox-herder"]);

// Cross-sell rows per book — slugs of 2-3 related books surfaced at the
// bottom of the detail page. Editorial choice, not algorithmic.
export const bookCrossSell = {
  "hanuman-chalisa": ["hanuman", "shiva-sati", "devi"],
  hanuman:           ["hanuman-chalisa", "jalandhara", "shiva-sati"],
  devi:              ["varaha", "shiva-sati", "samudra-manthan"],
  "shiva-sati":      ["jalandhara", "devi", "varaha"],
  jalandhara:        ["shiva-sati", "devi", "hanuman"],
  varaha:            ["samudra-manthan", "devi", "shiva-sati"],
  "samudra-manthan": ["varaha", "devi", "hanuman-chalisa"],
  "ox-herder":       ["hanuman-chalisa", "shiva-sati", "varaha"],
};

// Curated portrait set, narrative arc through the book.
export const bookSamplePanels = {
  hanuman: [
    { title: "Approaching Lanka",
      caption: "Across the cosmic ocean",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/hanuman/ad-portrait-1-lanka.jpg" },
    { title: "Baby with the sun",
      caption: "He mistakes a god for fruit",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/hanuman/ad-portrait-2-baby-sun.jpg" },
    { title: "The leap",
      caption: "His first miracle of strength",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/hanuman/ad-portrait-3-leap.jpg" },
    { title: "Bowing to Sri Ram",
      caption: "Where devotion finds its lord",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/hanuman/ad-portrait-4-bows.jpg" },
    { title: "The war begins",
      caption: "Ravana stands; the giants march",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/hanuman/ad-portrait-5-war.jpg" },
    { title: "Sanjeevani",
      caption: "He carries the mountain home",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/hanuman/ad-portrait-6-sanjeevani.jpg" },
  ],
  // Hanuman Chalisa sample panels — sourced from the live Meta carousel B-set
  // (same painterly art the visitor already saw in the ad → continuity into the
  // landing page). Native 1080x1080.
  "hanuman-chalisa": [
    { title: "Devotional portrait", caption: "The face of the seeker",
      aspect: "square",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/hanuman-chalisa/ad-1-hook.jpg" },
    { title: "Ram in the heart", caption: "Sītā-Rāma reside within",
      aspect: "square",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/hanuman-chalisa/ad-2-ram-in-heart.jpg" },
    { title: "Cosmic form", caption: "The vast and the loving made one",
      aspect: "square",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/hanuman-chalisa/ad-3-cosmic-giant.jpg" },
    { title: "The hero", caption: "Strength in service of devotion",
      aspect: "square",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/hanuman-chalisa/ad-4-hero.jpg" },
    { title: "The protector", caption: "Guardian against fear and shadow",
      aspect: "square",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/hanuman-chalisa/ad-5-protector.jpg" },
    { title: "Reader at the doha", caption: "Opening with Tulsidas",
      aspect: "square",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/hanuman-chalisa/ad-6-reader-doha.jpg" },
  ],
  // Curated portrait set, page-number ordered (chronological in the book).
  "shiva-sati": [
    { title: "The tear",
      caption: "What grief carves into the world",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/shiva-sati/ad-portrait-1-tear.jpg" },
    { title: "Certainty",
      caption: "Before the fire, the resolve",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/shiva-sati/ad-portrait-2-certain.jpg" },
    { title: "The wedding",
      caption: "When time could be stilled",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/shiva-sati/ad-portrait-3-wedding.jpg" },
    { title: "A lotus behind the ear",
      caption: "Devotion in small things",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/shiva-sati/ad-portrait-4-lotus.jpg" },
    { title: "Into the flame",
      caption: "She does not look back",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/shiva-sati/ad-portrait-5-fire.jpg" },
    { title: "After",
      caption: "His silence becomes the sky",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/shiva-sati/ad-portrait-6-nebula.jpg" },
  ],
  // Curated from the organic FB+IG carousel (2026-04-23) — portrait 2:3.
  devi: [
    { title: "Devi rises", caption: "When the gods could not",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/devi/ad-1-hook.jpg" },
    { title: "Yoganidrā withdraws", caption: "She slips into yogic sleep",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/devi/ad-2-yoganidra-withdraws.jpg" },
    { title: "The asuras grow bold", caption: "Mahiṣa rises while she sleeps",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/devi/ad-3-demons-arrogant.jpg" },
    { title: "Yoganidrā's gaze", caption: "Awaiting the call",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/devi/ad-4-yoganidra-portrait.jpg" },
    { title: "Sapta Mātṛkās", caption: "Seven mothers manifest",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/devi/ad-5-saptamatrikas.jpg" },
    { title: "The lotus field", caption: "Yā devī sarva-bhūteṣu",
      url: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/devi/ad-6-lotus-field.jpg" },
  ],
};

// Songs that are different mixes/versions of the same canonical work.
// Group key = canonical slug; values = song titles to fold into one card.
// The first song in each list becomes the card's representative.
export const songVariantGroups = {
  // An ALBUM, not a set of versions: the members are its tracks, in running
  // order. `members_label`, `member_labels`, `intro` and `cover_url` are read
  // by /listen and /listen/:slug. Released 6 Oct 2026. `album_links` open the
  // whole album; each track's own links come from its `songs` row.
  "buddhist-mantras": {
    canonical_title: "Buddhist Mantras for Prayer and Meditation",
    is_album: true,
    members_label: "Tracks",
    intro:
      "Eight Buddhist mantras chanted by monks, one after another, about seventeen minutes in all. Choose a track to read what the mantra is.",
    cover_url:
      "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/music/buddhist-mantras-cover-v1.jpg",
    album_links: {
      spotify_url: "https://open.spotify.com/album/7D2yvLJsANYY9GEYNveImI",
      apple_music_url: "https://music.apple.com/album/6820112505",
      youtube_music_url:
        "https://music.youtube.com/playlist?list=OLAK5uy_l7QeS8yab1l4bsOvZ3UfEy3_NmxjIijN0",
    },
    members: [
      "Om Mani Padme Hum · Buddhist Mantras",
      "Om Vajrasattva Hum · Buddhist Mantras",
      "Om Tare Tuttare Ture Soha · Buddhist Mantras",
      "White Tara Mantra · Buddhist Mantras",
      "Manjushri Mantra · Buddhist Mantras",
      "Medicine Buddha Mantra · Buddhist Mantras",
      "Om Ami Dewa Hrih · Buddhist Mantras",
      "Gate Gate Paragate · Buddhist Mantras",
    ],
    member_labels: [
      "1 · Om Mani Padme Hum",
      "2 · Om Vajrasattva Hum",
      "3 · Om Tare Tuttare Ture Soha",
      "4 · White Tara Mantra",
      "5 · Manjushri Mantra",
      "6 · Medicine Buddha Mantra",
      "7 · Om Ami Dewa Hrih",
      "8 · Gate Gate Paragate",
    ],
  },
  "gayatri-mantra": {
    canonical_title: "Gayatri Mantra",
    canonical_anchor: {
      source: "Ṛg Veda · 3.62.10",
      line: "Tat savitur vareṇyaṃ bhargo devasya dhīmahi · Dhiyo yo naḥ pracodayāt",
      translation:
        "May we meditate on that radiant glory of the Sun · May it inspire our intelligence.",
    },
    members: ["Gayatri Mantra (Sunrise Chant)", "Gayatri Mantra"],
  },
  "heart-sutra": {
    canonical_title: "Heart Sutra",
    canonical_anchor: {
      source: "Prajñāpāramitā Hṛdaya Sūtra",
      line: "Gate gate pāragate pārasaṃgate bodhi svāhā",
      translation:
        "Gone, gone, gone beyond, gone wholly beyond — awakening, hail!",
    },
    members: [
      "Heart Sutra - Hindi Revealed",
      "Heart Sutra - Trance",
      "Heart Sutra - Ascenion",
    ],
  },
  "birth-of-ma-tara": {
    canonical_title: "Birth of Ma Tara",
    canonical_anchor: {
      source: "Tārā Tantra · Buddhist (Tibetan)",
      line: "Oṃ tāre tuttāre ture svāhā",
      translation:
        "She who liberates from suffering, swiftly — the saviouress mantra.",
    },
    members: [
      "Birth of Ma Tara - Hindi Devotional",
      "Birth of Ma Tara - English Meditation",
      "Birth of Ma Tara - Monks Chant",
    ],
  },
  "gate-gate-paragate": {
    canonical_title: "Gate Gate Pāragate",
    canonical_anchor: {
      source: "Prajñāpāramitā Hṛdaya Sūtra (closing mantra)",
      line: "Gate gate pāragate pārasaṃgate bodhi svāhā",
      translation: "Gone, gone, gone beyond, gone wholly beyond — awakening!",
    },
    members: [
      "Gate Gate Paragate",
      "Gate Gate Paragate - Trance Mix",
      "Gate Gate Paragate (Ritual)",
    ],
  },
  "asato-maa-sad-gamaya": {
    canonical_title: "Asato Mā Sad Gamaya",
    canonical_anchor: {
      source: "Bṛhadāraṇyaka Upaniṣad",
      verse: "1.3.28",
      line: "Asato mā sad gamaya · Tamaso mā jyotir gamaya · Mṛtyor mā amṛtaṃ gamaya",
      translation:
        "From the unreal lead me to the real · From darkness to light · From death to deathlessness.",
    },
    members: [
      "Asadma Sad Gamaya Ascension Mix",
      "Asato Maa Sad Gamaya Ethereal Flow",
    ],
  },
  "dheere-dheere": {
    canonical_title: "Dheere Dheere Re Mana",
    canonical_anchor: {
      source: "Kabir · Dohe (Sant tradition)",
      line: "Dhīre dhīre re manā, dhīre sab kuch hoy",
      translation:
        "Slowly, slowly, my mind — slowly all things come to pass.",
    },
    members: [
      "Dheere Dheere Ra Mann - Kabir Folk Song",
      "Dheere Dheere Ra Mann",
    ],
  },
};

// Songs with a hand-authored anchor that aren't part of a variant group.
export const songAnchors = {
  "hanuman-chalisa": {
    source: "Hanuman Chalisa · Tulsidas",
    line: "Jai Hanumān gyān guṇ sāgar",
    translation: "Hail Hanuman, ocean of knowledge and virtue.",
  },
  "gayatri-mantra": {
    source: "Ṛg Veda · 3.62.10",
    line: "Tat savitur vareṇyaṃ bhargo devasya dhīmahi · Dhiyo yo naḥ pracodayāt",
    translation:
      "May we meditate on that radiant glory of the Sun · May it inspire our intelligence.",
  },
  "om-mani-padma-hum-monastic-chant": {
    source: "Mahāyāna Buddhist · Avalokiteśvara mantra",
    line: "Oṃ maṇi padme hūṃ",
    translation:
      "The jewel in the lotus — boundless compassion in the open heart.",
  },
  "om-mani-padme-hum-tribal-resonance": {
    source: "Mahāyāna Buddhist · Avalokiteśvara mantra",
    line: "Oṃ maṇi padme hūṃ",
    translation:
      "The jewel in the lotus — boundless compassion in the open heart.",
  },
  "mere-giridhar-gopal-meera-bhajan": {
    source: "Meera Bai · Bhakti tradition",
    line: "Mere to Giridhar Gopal, dūsaro na koī",
    translation: "Mine alone is Giridhar Gopal, no other.",
  },
  shabri: {
    source: "Aranya Kanda · Valmiki Ramayana",
    verse: "3.74",
    line: "Pratīkṣamāṇā bhaktyā tvāṃ nirvṛtā jīvitāntake",
    translation:
      "Awaiting you in devotion, she found peace at the end of her life.",
  },
  "ॐ-मणि-पद्मे-हूँ": {
    source: "Mahāyāna Buddhist · Avalokiteśvara mantra",
    line: "Oṃ maṇi padme hūṃ",
    translation:
      "The jewel in the lotus — boundless compassion in the open heart.",
  },
  "श्रीगुरु-चरन-सरोज-रज": {
    source: "Hanuman Chalisa · opening doha · Tulsidas",
    line: "Śrī guru charan saroj raj nij man mukur sudhāri",
    translation:
      "With the dust of my guru's lotus feet, I polish the mirror of my mind.",
  },
};

// Verified social + streaming destinations. Single source of truth — used by
// /follow, the footer, and any "share to" flows. Pull IDs from env in
// production code; for static surfaces, the URLs alone are enough.
export const channels = [
  {
    id: "instagram",
    label: "Instagram",
    handle: "@dharmaweave",
    url: "https://instagram.com/dharmaweave",
    note: "11.7K followers · primary engagement",
    family: "social",
  },
  {
    id: "facebook",
    label: "Facebook",
    handle: "DharmaWeave",
    url: "https://facebook.com/DharmaWeave",
    note: "Share-driven distribution",
    family: "social",
  },
  {
    id: "x",
    label: "X",
    handle: "@dharmaweave",
    url: "https://x.com/dharmaweave",
    note: "Daily posts · early stage",
    family: "social",
  },
  {
    id: "youtube",
    label: "YouTube",
    handle: "@dharmaweave",
    url: "https://youtube.com/@dharmaweave",
    note: "Shorts, reels, music videos",
    family: "video",
  },
  {
    id: "youtube-epics",
    label: "YouTube · Epics",
    handle: "@dharmaweaveepics",
    url: "https://youtube.com/@dharmaweaveepics",
    note: "Long-form sacred films (10–60 min)",
    family: "video",
  },
  {
    id: "youtube-music",
    label: "YouTube Music",
    handle: "DharmaWeave",
    url: "https://music.youtube.com/channel/UCaS3proOvHUMftEv_VTURSA",
    note: "Auto-generated artist channel",
    family: "music",
  },
  {
    id: "spotify",
    label: "Spotify",
    handle: "DharmaWeave",
    url: "https://open.spotify.com/artist/4Kg2Tc3I1sC1zMPiwYeX2x",
    note: "Artist page · 22 tracks",
    family: "music",
  },
  {
    id: "apple-music",
    label: "Apple Music",
    handle: "DharmaWeave",
    url: "https://music.apple.com/artist/1864134508",
    note: "Artist page",
    family: "music",
  },
  {
    id: "substack",
    label: "Substack",
    handle: "@dharmaweave",
    url: "https://substack.com/@dharmaweave",
    note: "Newsletter",
    family: "writing",
  },

  // WhatsApp channels — one master + 5 per-faith. The master is a curated
  // showcase (one post/day rotating across traditions); the faith channels are
  // deep streams. Same daily flagship piece appears on master AND its faith
  // channel; supplementary pieces only on the faith channel. URLs are populated
  // after Anant creates the channels in WhatsApp Business app — the Follow
  // page filters out empty-url channels so this section only appears when ready.
  {
    id: "whatsapp-master",
    label: "DharmaWeave",
    handle: "Daily wisdom · all traditions",
    url: "https://whatsapp.com/channel/0029Vb81AsXFnSz3n6f6qj0I",
    note: "One curated verse a day, rotating across the five traditions.",
    family: "messaging",
  },
  {
    id: "whatsapp-hindu",
    label: "WhatsApp · Hinduism",
    handle: "Daily wisdom",
    url: "",
    note: "Vedas, Upanishads, Gita — verse, image, festival reminders.",
    family: "messaging",
  },
  {
    id: "whatsapp-buddhist",
    label: "WhatsApp · Buddhism",
    handle: "Daily wisdom",
    url: "",
    note: "Pali suttas, Mahayana sutras, the bodhisattva path.",
    family: "messaging",
  },
  {
    id: "whatsapp-zen",
    label: "WhatsApp · Zen",
    handle: "Daily haiku",
    url: "",
    note: "Bashō, Dōgen, Hakuin — three lines, sometimes one.",
    family: "messaging",
  },
  {
    id: "whatsapp-christian",
    label: "WhatsApp · Christianity",
    handle: "Daily wisdom",
    url: "",
    note: "Gospels, Psalms, the desert fathers, the mystics.",
    family: "messaging",
  },
  {
    id: "whatsapp-sufi",
    label: "WhatsApp · Sufism",
    handle: "Daily wisdom",
    url: "",
    note: "Rumi, Hafiz, Ibn Arabi — the way of love.",
    family: "messaging",
  },
];

// Site-wide.
export const site = {
  name: "DharmaWeave",
  tagline: "Sacred stories, woven with light.",
  contact_email: "contact@tara-ventures.com",
  parent: { name: "Tara Ventures", url: "https://tara-ventures.com" },
  // Gold-silver woven knot — the mark used on the graphic novels.
  logo_icon:
    "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/logo-icon.png",
  logo_lockup:
    "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/logo-lockup.png",
};
